import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatINR, packageLabel, todayStr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function EditBillDialog({ bill, periods, open, onOpenChange, onDone }) {
  const [period, setPeriod] = useState("1");
  const [amount, setAmount] = useState("");
  const [startDate, setStartDate] = useState(todayStr());
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (bill) {
      setPeriod(String(bill.period_months));
      setAmount(String(bill.amount));
      setStartDate(bill.start_date || todayStr());
      setNote(bill.note || "");
    }
  }, [bill?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!bill) return null;

  const submit = async () => {
    setLoading(true);
    try {
      await api.put(`/bills/${bill.id}`, {
        period_months: Number(period),
        amount: Number(amount),
        start_date: startDate,
        note,
      });
      toast.success("Bill updated");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-bill-dialog">
        <DialogHeader>
          <DialogTitle>Edit Bill</DialogTitle>
          <DialogDescription>
            {bill.customer_name} · {bill.customer_code} · {packageLabel(bill.service_type, bill.package)}
            <br />
            <span className="text-xs">Paid so far: {formatINR(bill.paid_amount || 0)} — new amount can&apos;t go below this.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="edit-bill-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p} value={String(p)}>{p} month{p > 1 ? "s" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount (₹)</Label>
            <Input data-testid="edit-bill-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label>Start Date</Label>
            <Input data-testid="edit-bill-date" type="date" max={todayStr()} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea data-testid="edit-bill-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="edit-bill-save" disabled={loading || !amount} onClick={submit} className="bg-red-600 hover:bg-red-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
